import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import { useEffect, useRef } from "react";

import MDBox from "@/components/atoms/MDBox";
import { OrderFormData } from "@/components/screens/OrderFormPage/types";
import useToast from "@/contexts/toast-context";
import { IOrderInput } from "@/interfaces/order.interfaces";
import { ItemDataTabMessages } from "@/messages/order-form-page/item-data-tab.messages";

import { ItemTable } from "./components/ItemsTable";
import { NewProductPayload } from "./components/NewProductDialog";
import {
  applyCampaignPriceToItem,
  getOrderItemTotals,
  resolveCampaignNetUnitPrice,
  resolveEligibleCampaign,
  transformSkuToItem,
} from "./form-data";

type Props = {
  messages: ItemDataTabMessages;
  order: IOrderInput | null;
};

export default function ItemDataTab({ messages, order }: Props) {
  const { showToast } = useToast();
  const { values, setFieldValue } = useFormikContext<OrderFormData>();
  const { items } = values;
  const lastEligibilityKey = useRef<string>("");

  useEffect(() => {
    const { netTotal, couponDiscount } = getOrderItemTotals(items);
    setFieldValue("orderAmount", netTotal);
    setFieldValue("discountCouponAmount", couponDiscount);
  }, [items, setFieldValue]);

  useEffect(() => {
    if (values.disabledButton) return;

    const campaignId = values.campaignId ?? order?.campaignId ?? null;
    const couponCode = values.discountCouponCode || undefined;
    const contactId = values.contactId ?? order?.contactId ?? null;
    const eligibilityKey = `${campaignId || ""}|${couponCode || ""}|${contactId || ""}`;

    if (!campaignId && !couponCode) return;
    if (lastEligibilityKey.current === eligibilityKey) return;

    let cancelled = false;
    const applyEligibility = async () => {
      const campaign = await resolveEligibleCampaign(campaignId, couponCode, contactId);
      if (cancelled) return;

      lastEligibilityKey.current = eligibilityKey;
      const updatedItems = values.items.map((item) => applyCampaignPriceToItem(item, campaign));
      setFieldValue("items", updatedItems);
    };

    applyEligibility();
    return () => {
      cancelled = true;
    };
  }, [
    order?.campaignId,
    order?.contactId,
    setFieldValue,
    values.campaignId,
    values.contactId,
    values.disabledButton,
    values.discountCouponCode,
    values.items,
  ]);

  const handleAddProduct = async ({
    sku,
    product,
    quantity,
    discountCouponCode,
    campaignId: payloadCampaignId,
    campaignNetUnitPrice,
  }: NewProductPayload) => {
    const skuExists = values.items.some((item) => item.skuCode === sku.code);
    if (skuExists) {
      showToast({ text: messages.skuAlreadyAdded, type: "error" });
      return;
    }

    const campaignId = payloadCampaignId ?? values.campaignId ?? order?.campaignId ?? null;
    const couponCode = discountCouponCode || values.discountCouponCode || undefined;
    const contactId = values.contactId ?? order?.contactId ?? null;
    const resolvedNetUnitPrice =
      campaignNetUnitPrice && campaignNetUnitPrice > 0
        ? campaignNetUnitPrice
        : await resolveCampaignNetUnitPrice(sku, quantity, campaignId, couponCode, contactId);

    const transformedItem = transformSkuToItem(sku, order, product?.needComment || false, {
      quantity,
      discountCouponCode: couponCode,
      campaignId,
      campaignNetUnitPrice: resolvedNetUnitPrice,
    });
    setFieldValue("items", [...values.items, transformedItem]);
  };

  return (
    <MDBox mt={2}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 12 }}>
          <ItemTable messages={messages} handleAddProduct={handleAddProduct} />
        </Grid>
      </Grid>
    </MDBox>
  );
}
